import { useEffect, useState } from "react";
import API from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { to_email: "", cc_emails: "", subject: "", message: "", branding: {} };

/* Opens the "Email quote to client" dialog; loads defaults from /quotes/:id/email-defaults when opened.
   Used by the full quote page and the request/job quote drawer. */
export default function QuoteEmailDialog({ quoteId, open, onOpenChange, onSent }) {
  const [form, setForm] = useState(EMPTY);
  const [info, setInfo] = useState({ send_count: 0, last_sent_at: "" });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !quoteId) return;
    let alive = true;
    setLoading(true);
    API.get(`/quotes/${quoteId}/email-defaults`)
      .then(({ data }) => {
        if (!alive) return;
        setForm({ to_email: data.to_email || "", cc_emails: "", subject: data.subject || "", message: data.message || "", branding: data.branding || {} });
        setInfo({ send_count: data.send_count || 0, last_sent_at: data.last_sent_at || "" });
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Email-from-platform is not enabled. Turn it on in Settings > Quote Email.");
        onOpenChange(false);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!form.to_email || !form.to_email.includes("@")) { toast.error("Please enter a valid recipient email"); return; }
    setSending(true);
    try {
      await API.post(`/quotes/${quoteId}/send-email`, { to_email: form.to_email, cc_emails: form.cc_emails, subject: form.subject, message: form.message, branding: form.branding || {} });
      toast.success(`Quote emailed to ${form.to_email}`);
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const brandName = form.branding?.company_name;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="email-quote-dialog">
        <DialogHeader>
          <DialogTitle>Email Quote to Client</DialogTitle>
          <DialogDescription>
            {info.send_count > 0
              ? `Sent ${info.send_count} time${info.send_count > 1 ? "s" : ""}${info.last_sent_at ? ` — last on ${new Date(info.last_sent_at).toLocaleDateString("en-GB")}` : ""}.`
              : "Sends the client PDF with a branded cover email."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">To</Label>
              <Input type="email" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} placeholder="client@example.com" className="mt-1" data-testid="email-to-input" />
            </div>
            <div>
              <Label className="text-xs">CC (comma-separated)</Label>
              <Input value={form.cc_emails} onChange={(e) => setForm({ ...form, cc_emails: e.target.value })} placeholder="Optional" className="mt-1" data-testid="email-cc-input" />
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" data-testid="email-subject-input" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} className="mt-1" data-testid="email-message-input" />
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid="quote-email-branding-note">
              {brandName ? <>Branded as <span className="font-semibold text-foreground">{brandName}</span> (this client's saved branding, inherited from the main client where applicable).</> : "Sent with your company branding — set a client brand on the client record to white-label it."}
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="email-cancel-btn">Cancel</Button>
              <Button onClick={send} disabled={sending} data-testid="email-send-btn">
                <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Send Email"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
